/**
 * Codigos de error estables compartidos entre servidor y cliente.
 *
 * El cliente reacciona al CODIGO, nunca al texto. El texto en español existe
 * para mostrarlo tal cual cuando no hay un manejo especifico, y puede cambiar
 * sin romper nada.
 */
export const ERROR_CODES = [
    // Autenticacion y autorizacion
    'UNAUTHENTICATED',
    'INVALID_CREDENTIALS',
    'ACCOUNT_LOCKED',
    'ACCOUNT_DISABLED',
    'SESSION_EXPIRED',
    'FORBIDDEN',
    'NO_STORE_ACCESS',
    // Validacion
    'VALIDATION_ERROR',
    'NOT_FOUND',
    'CONFLICT',
    // Reglas de negocio del punto de venta
    'PRODUCT_NOT_FOUND',
    'PRODUCT_UNAVAILABLE',
    // Se toco un producto BORRADO del catalogo. Solo puede llegar por un camino
    // que no pasa por el catalogo —hoy, aprobar una entrada capturada antes del
    // borrado— porque ninguna pantalla lo lista. Lo lanzan el trigger de
    // `store_products` y `restore_product`.
    'PRODUCT_DELETED',
    'INSUFFICIENT_STOCK',
    'EMPTY_SALE',
    'INSUFFICIENT_PAYMENT',
    'DISCOUNT_EXCEEDS_TOTAL',
    'SALE_ALREADY_CANCELLED',
    'BARCODE_TAKEN',
    'SKU_TAKEN',
    // Se pidio cobrar a mayoreo un producto que no tiene precio de mayoreo. Se
    // RECHAZA en vez de caer a menudeo: cobrar algo distinto de lo que se eligio
    // no deja ningun error que ver, solo un cliente que paga mal.
    'WHOLESALE_NOT_SET',
    // Caja
    'CASH_SESSION_OPEN', // ya hay una caja abierta para este usuario
    // El cajon es de la SUCURSAL: ya lo abrio alguien mas y no se abre un segundo
    // sobre el mismo dinero. No es un error del que lo intenta — es la respuesta
    // "ya esta, puedes cobrar". Trae `openedByName` y `openedAt` en el detalle.
    'CASH_SESSION_STORE_OPEN',
    'CASH_SESSION_MISSING', // se intenta cobrar sin caja abierta
    'CASH_SESSION_CLOSED',
    // Quien no tiene turno propio cobra en el que abrio otro; con dos cajones
    // abiertos a la vez no hay forma de saber en cual cae el efectivo.
    'CASH_SESSION_AMBIGUOUS',
    // Sucursales
    //
    // VAN AQUI Y NO EN `DB_MESSAGES` DE `lib/api.ts`, y la diferencia no es de
    // estilo: `toApiError` solo conserva el `using detail` de la base en la rama
    // de los codigos de ESTE catalogo. Un codigo que solo vive en `DB_MESSAGES`
    // llega al cliente convertido en `INTERNAL_ERROR` y con el detalle TIRADO —o
    // sea, sin el limite ni el total, que es justo lo que la pantalla necesita
    // enseñar—.
    //
    // Se acabaron las sucursales del plan contratado. Trae `limit` y `current` en
    // el detalle. No es un error de quien lo intenta: es una conversacion
    // comercial, y la pantalla la trata como tal.
    'STORE_LIMIT_REACHED',
    // Se quiso eliminar la UNICA sucursal activa del negocio. Un negocio sin
    // sucursal no puede cobrar, y la sesion se quedaria sin tienda con la
    // aplicacion pidiendo datos de una que ya no existe.
    'STORE_LAST_ACTIVE',
    // Se quiso eliminar una sucursal con el cajon abierto. Ese efectivo esta sin
    // arquear: primero se cierra el turno. Trae `openShifts` en el detalle.
    'STORE_HAS_OPEN_SHIFT',
    // Se quiso traer a una sucursal un producto que el negocio dio de baja.
    // Descontinuar es de la organizacion entera, no de una sucursal.
    'PRODUCT_ARCHIVED',
    // Se quiso dar de alta -o reactivar- un producto con una medida que esa
    // sucursal no cobra: una bodega vende por tonelada y por bulto, una tienda de
    // abarrotes por pieza y por kilo. Trae `unit`, `profile` y `allowed`.
    'UNIT_NOT_IN_PROFILE',
    // Se quiso cambiar de tipo una sucursal que tiene productos activos con una
    // medida que el tipo nuevo no usa. Trae `count` y `units` en el detalle, que
    // es lo que convierte "no se puede" en "corrige estos y vuelve".
    'UNIT_PROFILE_CONFLICT',
    // Entradas de mercancia
    'PURCHASE_NOT_FOUND',
    'PURCHASE_ALREADY_CANCELLED',
    // Solo se cancela la ultima entrada viva de cada producto: el costo previo
    // que se restaura deja de ser el correcto si despues entro otra compra.
    'PURCHASE_NOT_LATEST',
    // Credito (fiado)
    'CUSTOMER_REQUIRED', // se intenta fiar sin decir a quien
    'CUSTOMER_INACTIVE',
    'CREDIT_NOT_ALLOWED', // limite en cero: a este cliente no se le fia
    'CREDIT_LIMIT_EXCEEDED',
    'PAYMENT_EXCEEDS_BALANCE',
    // Infraestructura
    'RATE_LIMITED',
    'INTERNAL_ERROR',
];
export const ERROR_MESSAGES = {
    UNAUTHENTICATED: 'Inicia sesion para continuar.',
    INVALID_CREDENTIALS: 'Usuario o contraseña incorrectos.',
    ACCOUNT_LOCKED: 'Cuenta bloqueada temporalmente por intentos fallidos. Espera unos minutos.',
    ACCOUNT_DISABLED: 'Esta cuenta esta desactivada. Habla con el dueño.',
    SESSION_EXPIRED: 'Tu sesion expiro. Vuelve a iniciar sesion.',
    FORBIDDEN: 'No tienes permiso para hacer esto.',
    NO_STORE_ACCESS: 'No tienes acceso a esta sucursal.',
    VALIDATION_ERROR: 'Hay datos incorrectos en el formulario.',
    NOT_FOUND: 'No se encontro lo que buscabas.',
    CONFLICT: 'Ese registro ya existe.',
    PRODUCT_NOT_FOUND: 'Ese producto no esta en el catalogo.',
    PRODUCT_UNAVAILABLE: 'Ese producto esta marcado como no disponible.',
    // Dice QUE HACER, porque el producto ya no esta en ninguna pantalla y sin la
    // salida esto seria un callejon sin salida (§13).
    PRODUCT_DELETED: 'Ese producto se borro del catalogo. Si lo vuelves a manejar, dalo de alta otra vez: su clave y sus codigos quedaron libres.',
    INSUFFICIENT_STOCK: 'No hay existencia suficiente.',
    EMPTY_SALE: 'Agrega al menos un producto antes de cobrar.',
    INSUFFICIENT_PAYMENT: 'El efectivo recibido no cubre el total.',
    DISCOUNT_EXCEEDS_TOTAL: 'El descuento no puede ser mayor al total.',
    SALE_ALREADY_CANCELLED: 'Esa venta ya estaba cancelada.',
    BARCODE_TAKEN: 'Ese codigo de barras ya pertenece a otro producto.',
    SKU_TAKEN: 'Ese codigo interno ya esta en uso.',
    WHOLESALE_NOT_SET: 'Ese producto no tiene precio de mayoreo. Ponselo desde Inventario y vuelve a cobrar.',
    CASH_SESSION_OPEN: 'Ya tienes una caja abierta. Cierrala antes de abrir otra.',
    CASH_SESSION_STORE_OPEN: 'La caja de esta sucursal ya esta abierta. Ya puedes cobrar.',
    CASH_SESSION_MISSING: 'Abre la caja antes de empezar a cobrar.',
    CASH_SESSION_CLOSED: 'Esa caja ya fue cerrada.',
    CASH_SESSION_AMBIGUOUS: 'Hay mas de una caja abierta en la sucursal y no se sabe en cual entra este ' +
        'dinero. Que cierren una antes de seguir cobrando.',
    // El texto es el de respaldo. La pantalla de Sucursales trata este codigo
    // aparte y enseña la tarjeta con el contacto de Aziru, porque no es una falla
    // que corregir sino un modulo que contratar.
    STORE_LIMIT_REACHED: 'Llegaste al numero de sucursales de tu plan. Ampliarlo es un modulo adicional: ' +
        'contacta a Aziru.',
    // Los dos dicen QUE HACER, no solo que no se puede: un error sin salida es un
    // callejon sin salida (§13).
    STORE_LAST_ACTIVE: 'Es la unica sucursal activa del negocio y no se puede eliminar: sin sucursal no ' +
        'se puede cobrar. Abre otra primero.',
    STORE_HAS_OPEN_SHIFT: 'Esa sucursal tiene el cajon abierto. Cierra el turno —ahi se cuenta el efectivo y ' +
        'se firma la diferencia— y vuelve a intentarlo.',
    PRODUCT_ARCHIVED: 'Ese producto esta descontinuado en el negocio. Reactivalo desde Inventario antes ' +
        'de traerlo a esta sucursal.',
    UNIT_NOT_IN_PROFILE: 'Esa medida no se usa en esta sucursal. Una bodega cobra por tonelada y por bulto ' +
        'de 25 y 50 kg; una tienda de abarrotes, por pieza, kilo, litro, paquete o caja. ' +
        'Se cambia en Sucursales.',
    UNIT_PROFILE_CONFLICT: 'Esta sucursal tiene productos activos con una medida que el tipo nuevo no usa. ' +
        'Descontinualos o corrigeles la medida desde Inventario y vuelve a intentar.',
    PURCHASE_NOT_FOUND: 'No se encontro esa entrada de mercancia.',
    PURCHASE_ALREADY_CANCELLED: 'Esa entrada ya estaba cancelada.',
    PURCHASE_NOT_LATEST: 'Hay una entrada mas reciente de alguno de estos productos. Cancela primero la ultima.',
    CUSTOMER_REQUIRED: 'Elige a quien se le fia antes de cobrar.',
    CUSTOMER_INACTIVE: 'Ese cliente esta desactivado.',
    CREDIT_NOT_ALLOWED: 'A este cliente no se le fia.',
    CREDIT_LIMIT_EXCEEDED: 'Con esta venta se pasaria de su limite de credito.',
    PAYMENT_EXCEEDS_BALANCE: 'El abono es mayor a lo que debe.',
    RATE_LIMITED: 'Demasiados intentos. Espera un momento.',
    INTERNAL_ERROR: 'Ocurrio un error inesperado. Intenta de nuevo.',
};
export function isErrorCode(value) {
    return typeof value === 'string' && ERROR_CODES.includes(value);
}
export function messageForCode(code) {
    return ERROR_MESSAGES[code];
}
//# sourceMappingURL=errors.js.map